import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Send, PenTool, AlertTriangle, ExternalLink, Download, Pencil } from 'lucide-react';
import {
  useMesProformas, useAttacherSignature, useSoumettreProforma,
} from '@/hooks/useCommercial';
import { commercialApi, type Proforma } from '@/api/commercial';
import { toast } from '@/stores/toastStore';
import { Badge, Modal, ModalHeader, type BadgeTone } from './_ui';

/**
 * SIMG-COMMERCIAL-LEADBETON-2026-001 — Liste des proformas du commercial.
 *
 * On expose :
 *   - Statut visuel (devis / en_validation_daf / renvoyée à corriger)
 *   - Bandeau motif si renvoyée par le DAF
 *   - Bouton "Signature client" (attache URL/photo puis soumet au DAF)
 *   - Bouton "Soumettre" si signature déjà attachée
 */

const STATUS_LABELS: Record<string, { label: string; tone: BadgeTone }> = {
  devis:             { label: 'Devis', tone: 'neutral' },
  en_validation_daf: { label: 'Chez le DAF', tone: 'info' },
  confirmee:         { label: 'Confirmée', tone: 'success' },
  en_fabrication:    { label: 'En fabrication', tone: 'info' },
  prete_au_parc:     { label: 'Prête au parc', tone: 'info' },
  expediee:          { label: 'Expédiée', tone: 'info' },
  livree:            { label: 'Livrée', tone: 'success' },
  facturee:          { label: 'Facturée', tone: 'success' },
  annulee:           { label: 'Annulée', tone: 'danger' },
};

export const MesProformasPage = () => {
  const [sp] = useSearchParams();
  const filtre = sp.get('statut'); // 'devis' | 'en_validation_daf' | 'renvoyees' | null

  const statutBackend = filtre === 'renvoyees' ? 'devis' : filtre ?? undefined;
  const { data = [], isLoading } = useMesProformas(statutBackend);

  const rows = useMemo(() => {
    if (filtre === 'renvoyees') return data.filter((p) => p.renvoyeeMotif !== null);
    if (filtre === 'devis')     return data.filter((p) => p.renvoyeeMotif === null);
    return data;
  }, [data, filtre]);

  const [signer, setSigner]     = useState<Proforma | null>(null);

  return (
    <div>
      <h1 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 800 }}>Mes proformas</h1>
      <p style={{ margin: '0 0 16px', color: 'var(--tx-3, #6B7280)', fontSize: 13 }}>
        {filtreLabel(filtre)}
      </p>

      {isLoading && <div style={{ padding: 24, textAlign: 'center', color: 'var(--tx-3)' }}>Chargement…</div>}

      {!isLoading && rows.length === 0 && (
        <div style={{ background: '#fff', border: '1px solid var(--bd-1, #E5E7EB)', borderRadius: 12, padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 14, color: 'var(--tx-3, #6B7280)' }}>Aucune proforma dans ce filtre.</div>
        </div>
      )}

      <div style={{ display: 'grid', gap: 12 }}>
        {rows.map((p) => (
          <ProformaCard
            key={p.id}
            proforma={p}
            onSigner={() => setSigner(p)}
          />
        ))}
      </div>

      {signer && (
        <SignatureDialog
          proforma={signer}
          onClose={() => setSigner(null)}
        />
      )}
    </div>
  );
};

function filtreLabel(f: string | null): string {
  if (f === 'renvoyees')         return 'Renvoyées par le DAF — à corriger';
  if (f === 'devis')             return 'Devis (à faire signer ou à relancer)';
  if (f === 'en_validation_daf') return 'En attente de validation DAF';
  return 'Toutes tes proformas récentes';
}

function ProformaCard({ proforma: p, onSigner }: { proforma: Proforma; onSigner: () => void }) {
  const nav = useNavigate();
  const soumettre = useSoumettreProforma();
  const [downloading, setDownloading] = useState(false);
  const stat = STATUS_LABELS[p.statut] ?? { label: p.statut, tone: 'neutral' as const };
  const ht  = Number(p.montantHtFcfa);
  const ligneCount = p.lignes?.length ?? 0;
  const modifiable = p.statut === 'devis' || p.statut === 'en_validation_daf';

  const telecharger = async () => {
    setDownloading(true);
    try {
      await commercialApi.telechargerPdf(p.id, p.reference);
      toast.success('PDF téléchargé', p.reference);
    } catch {
      // Toast d'erreur déjà émis par l'interceptor axios global.
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div style={{
      background: '#fff', border: '1px solid var(--bd-1, #E5E7EB)', borderRadius: 12,
      padding: 14, display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 15 }}>{p.reference}</div>
          <div style={{ fontSize: 13, color: 'var(--tx-2, #374151)' }}>
            {p.client?.fullName ?? '—'}
            {p.client?.companyName && <span style={{ color: 'var(--tx-3, #6B7280)' }}> · {p.client.companyName}</span>}
          </div>
          <div style={{ fontSize: 12, color: 'var(--tx-3, #6B7280)', marginTop: 2 }}>
            {ligneCount} ligne{ligneCount > 1 ? 's' : ''} · Créée le {new Date(p.createdAt).toLocaleDateString('fr-FR')}
          </div>
        </div>
        <Badge tone={stat.tone}>{stat.label}</Badge>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div style={{ fontSize: 12, color: 'var(--tx-3, #6B7280)' }}>Total HT</div>
        <div style={{ fontWeight: 800, fontSize: 18, fontFamily: 'monospace' }}>
          {ht.toLocaleString('fr-FR')} FCFA
        </div>
      </div>

      {/* Motif renvoi visible du commercial */}
      {p.renvoyeeMotif && (
        <div style={{
          background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8,
          padding: 10, fontSize: 12, color: '#991B1B',
          display: 'flex', gap: 6, alignItems: 'flex-start',
        }}>
          <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <div style={{ fontWeight: 700, marginBottom: 2 }}>
              Renvoyée par le DAF — {p.renvoyeeLe && new Date(p.renvoyeeLe).toLocaleDateString('fr-FR')}
            </div>
            <div>{p.renvoyeeMotif}</div>
          </div>
        </div>
      )}

      {/* Actions selon statut */}
      <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
        {/* Le PDF est dispo à tous les statuts — imprimer, envoyer WhatsApp/mail, archiver */}
        <button
          onClick={telecharger}
          disabled={downloading}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '8px 12px', borderRadius: 8, border: '1px solid var(--bd-1)',
            background: '#fff', color: 'var(--tx-2)', fontSize: 13, cursor: 'pointer',
          }}
        >
          <Download size={13} /> {downloading ? 'Génération…' : 'Télécharger PDF'}
        </button>

        {/* Modification autorisée en devis OU en_validation_daf (gèle après confirmée) */}
        {modifiable && (
          <button
            onClick={() => nav(`/commercial/nouvelle?edit=${p.id}`)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 12px', borderRadius: 8, border: '1px solid var(--bd-1)',
              background: '#fff', color: 'var(--tx-2)', fontSize: 13, cursor: 'pointer',
            }}
          >
            <Pencil size={13} /> Modifier
          </button>
        )}

        {p.statut === 'devis' && (
          <>
            {p.signatureClientUrl ? (
              <>
                <a
                  href={p.signatureClientUrl} target="_blank" rel="noreferrer"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '8px 12px', borderRadius: 8, border: '1px solid var(--bd-1)',
                    background: '#fff', color: 'var(--tx-2)', fontSize: 13, textDecoration: 'none',
                  }}
                >
                  <ExternalLink size={13} /> Voir signature
                </a>
                <button
                  onClick={() => soumettre.mutate(p.id)}
                  disabled={soumettre.isPending}
                  style={{
                    flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    padding: '10px 12px', borderRadius: 8, border: 'none',
                    background: 'var(--pr-1, #1F4E79)', color: '#fff', fontWeight: 700, fontSize: 13,
                    cursor: 'pointer',
                  }}
                >
                  <Send size={14} /> Soumettre au DAF
                </button>
              </>
            ) : (
              <button
                onClick={onSigner}
                style={{
                  flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  padding: '10px 12px', borderRadius: 8, border: 'none',
                  background: 'var(--pr-1, #1F4E79)', color: '#fff', fontWeight: 700, fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                <PenTool size={14} /> Signature client + soumettre
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function SignatureDialog({ proforma, onClose }: { proforma: Proforma; onClose: () => void }) {
  const [url, setUrl] = useState('');
  const attacher    = useAttacherSignature();
  const soumettre   = useSoumettreProforma();
  const valid       = url.trim().length > 10;

  const enchainer = async () => {
    if (!valid) return;
    await attacher.mutateAsync({ id: proforma.id, url: url.trim() });
    await soumettre.mutateAsync(proforma.id);
    onClose();
  };

  return (
    <Modal onClose={onClose}>
      <div style={{ maxWidth: 480, width: '100%', padding: 20 }}>
        <ModalHeader titre="Signature client" onClose={onClose} />
        <p style={{ fontSize: 13, color: 'var(--tx-3, #6B7280)', marginTop: 0 }}>
          Fais signer <strong>{proforma.reference}</strong> par le client, puis colle le lien
          (photo ou PDF partagé). Elle sera envoyée au DAF pour validation dans la foulée.
        </p>

        <label style={{ display: 'block' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-2)', marginBottom: 4 }}>
            URL signature (photo ou PDF)
          </div>
          <input
            type="url" value={url} onChange={(e) => setUrl(e.target.value)} autoFocus
            placeholder="https://…"
            style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--bd-2)', borderRadius: 8, fontSize: 14 }}
          />
        </label>

        <div style={{ marginTop: 20, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--bd-1)', background: '#fff', cursor: 'pointer' }}
          >
            Annuler
          </button>
          <button
            onClick={enchainer}
            disabled={!valid || attacher.isPending || soumettre.isPending}
            style={{
              padding: '8px 14px', borderRadius: 8, border: 'none',
              background: valid ? 'var(--pr-1)' : '#D1D5DB', color: '#fff', fontWeight: 700,
              cursor: valid ? 'pointer' : 'not-allowed',
            }}
          >
            {attacher.isPending || soumettre.isPending ? 'Envoi…' : 'Attacher et soumettre'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
